/* This chart looks quite similar to the SNP (relative effect) chart, but instead of showing the effect of each snp
separately, it will take multiple SNPs per trait, and display the likelihood of having a trait according to the effects
of all inputted variants put together. This chart should only be used in the case where multiple SNPs can be used
together to give a "hint" at predicting the likelihood of someone showing a trait, and where each SNP has a roughly
similar sized effect. Currently only used for the personality traits & lactose intolerance reports. */
<template>
   <div :data-cy="`${traitLabel}-combined-effect-chart`">
      <!-- Higher / lower gradient line and labels -->
      <!-- Labels may be visually hidden when we have a set of these charts which all use the same one and this can be read on the first -->
      <div class="flex items-end justify-between pb-3 uppercase" :aria-hidden="true">
         <div class="flex w-1/2 items-end pr-3">
            <span class="text-bolder pr-2 leading-none">-</span>
            <span class="text-lighter block text-sm leading-none"
               >{{ $t("dna-testing.trait_reports.less_likely") }} {{ props.content?.trait }}</span
            >
         </div>
         <div class="lg:w-1/5 lg:grow"></div>
         <div class="flex w-1/2 items-end justify-end pl-3 text-right">
            <span class="text-lighter block text-sm leading-none"
               >{{ $t("dna-testing.trait_reports.more_likely") }} {{ props.content?.trait }}</span
            >
            <span class="text-bolder pl-2 leading-none">+</span>
         </div>
      </div>
      <div class="relative h-3 w-full">
         <div
            class="absolute inset-0 rounded-full"
            :style="`background: linear-gradient(270deg, currentcolor, transparent 125%);`"
         ></div>
      </div>
      <!-- Position marker [theme class css in cms-themes.scss] -->
      <div v-if="missingSnpRsids.length < chartRsidKeys.length">
         <div class="relative" style="margin: 0 10%">
            <div
               data-cy="likelihood-marker"
               class="absolute top-0 h-6 w-2.5 rounded-md border-2"
               :class="`theme-${color}`"
               :style="`left: ${markerPosition}%; margin-top: -1.125rem;`"
            ></div>
         </div>
      </div>
      <!-- Explainer message for 'equal effect' charts -->
      <p
         v-if="props.content.combinationType === 'equalEffect'"
         class="pt-2 text-sm leading-snug"
         data-cy="equal-effect-explainer"
      >
         <span v-if="rsidsWithNoAssociatedAlleles > 0">
            <span v-if="rsidsWithNoAssociatedAlleles === 1">{{
               $t("dna-testing.trait_reports.rsid_no_associated_allele")
            }}</span>
            <span v-else>{{
               $t("dna-testing.trait_reports.rsids_no_associated_allele", { number: rsidsWithNoAssociatedAlleles })
            }}</span>
            <span>{{ rsidsWithNoAssociatedAlleles === chartRsidKeys.length ? ". " : "; " }}</span>
         </span>
         <span v-if="rsidsWithOneAssociatedAllele > 0">
            <span v-if="rsidsWithOneAssociatedAllele === 1">{{
               $t("dna-testing.trait_reports.rsid_one_associated_allele")
            }}</span>
            <span v-else>{{
               $t("dna-testing.trait_reports.rsids_one_associated_allele", { number: rsidsWithOneAssociatedAllele })
            }}</span>
            <span>{{
               rsidsWithNoAssociatedAlleles + rsidsWithOneAssociatedAllele === chartRsidKeys.length ? ". " : "; "
            }}</span>
         </span>
         <span v-if="rsidsWithTwoAssociatedAlleles > 0">
            <span v-if="rsidsWithTwoAssociatedAlleles === 1">{{
               $t("dna-testing.trait_reports.rsid_two_associated_alleles")
            }}</span>
            <span v-else
               >{{
                  $t("dna-testing.trait_reports.rsids_two_associated_alleles", {
                     number: rsidsWithTwoAssociatedAlleles,
                  })
               }}.</span
            >
         </span>
      </p>
      <!-- Aria message - explain without the visual of the chart -->
      <p v-if="missingSnpRsids.length < chartRsidKeys.length" class="sr-only" data-cy="combined-effect-aria-message">
         {{
            $t("dna-testing.trait_reports.we_predict_you_are", {
               number: chartRsidKeys.length - missingSnpRsids.length,
            })
         }}
         {{ ariaMarkerPositionDescription }} {{ props.content?.trait }}
      </p>
      <!-- Note any rsids where snp information is missing -->
      <p v-if="missingSnpRsids.length > 0" data-cy="missing-snps-explainer" class="pt-2 text-sm leading-snug">
         <span v-for="(rsid, i) in missingSnpRsids" :key="`missing-rsid-${rsid}`" class="text-bolder">
            <span>{{ rsid }}</span
            ><span v-if="i < missingSnpRsids.length - 1">, </span><span v-else>: </span>
         </span>
         <span v-if="missingSnpRsids.length === 1">{{ $t("dna-testing.trait_reports.missing_snp") }}</span>
         <span v-else>{{ $t("dna-testing.trait_reports.missing_snps") }}</span>
      </p>
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";

const { t: $t } = useI18n();

interface combinedSnpsChartContent {
   trait: string;
   // "EitherOr: any listed variant within the snps list makes this trait likely, none makes this unlikely",
   // "EqualEffect: listed variants within the snps list makes this trait more likely by a roughly equal amount, with 2 listed copies of that allele having twice the effect of 1"
   combinationType: "equalEffect" | "eitherOr";
   rsids: {
      [key: string]: string;
   };
}

// --------------- Props and state
const props = defineProps<{
   content: combinedSnpsChartContent;
   color: string;
   userVariants: { [key: string /* RSID */]: string /* Variant e.g. AA */ };
}>();

// --------------- Computed properties
const traitLabel = computed(() => {
   return props.content?.trait.replace(" ", "-").toLowerCase();
});

const chartRsidKeys = computed(() => {
   return Object.keys(props.content.rsids);
});

const missingSnpRsids = computed(() => {
   // We need to know if snps are missing/not a usable result so we can account for this in the chart
   return chartRsidKeys.value.filter((rsid) => {
      const variant = props.userVariants[rsid];
      return !variant || variant === "--";
   });
});

const allelesAssociatedWithTraitByRsid = computed(() => {
   // Return an object which for each rsid in this chart notes how many alleles (associated with the trait which is the subject of this chart)
   // the user has. So 3 rsids may be looked at with the user having rsA: 0, rsB: 1 and and rsC: 2 associated alleles
   const rsidsUserAllelesObject = {};
   for (let i = 0; i < chartRsidKeys.value.length; i++) {
      const rsid = chartRsidKeys.value[i];
      // exclude missing / not usable rsids from this list we'll use to count associations
      if (!missingSnpRsids.value.includes(rsid)) {
         const associatedAllele = props.content.rsids[rsid];
         const userAlleles = props.userVariants[rsid] ? props.userVariants[rsid].split("") : [];
         rsidsUserAllelesObject[rsid] = userAlleles.filter((a) => a === associatedAllele).length;
      }
   }
   return rsidsUserAllelesObject;
});

const markerPosition = computed(() => {
   const numberOfAllelesAtSnp = 2;
   // For the 'equal-effect' chart type; Return a number between 0 and 100 where 100 is the maximum associated alleles someone could
   // have (looking at these rsids) and 0 is the minimum. Where there are missing snps, the maximum number of alleles this user could
   // 'have' (that we can know about) should come down so their score is not artificially low
   const maximumTraitAssociatedAlleles: number =
      (chartRsidKeys.value.length - missingSnpRsids.value.length) * numberOfAllelesAtSnp;
   const numbersOfAssociatedAlleles = Object.values(allelesAssociatedWithTraitByRsid.value);
   let userTraitAssociatedAlleles = 0;
   if (numbersOfAssociatedAlleles.length > 0) {
      userTraitAssociatedAlleles = Number(numbersOfAssociatedAlleles.reduce((acc, val) => Number(acc) + Number(val)));
   }

   // console.log(props.content.combinationType, userTraitAssociatedAlleles, chartRsidKeys.value.length, missingSnpRsids.value.length);
   if (props.content.combinationType === "equalEffect") {
      const x = (Number(userTraitAssociatedAlleles) / maximumTraitAssociatedAlleles) * 100;
      return x;
   }
   // For the 'either-or' chart type (e.g. lactose intolerance) it's very simple - we return either 'likely' [position 85] tolerant
   // with ANY associated variant or 'unlikely' [position 15] with no associated variants as any of several mutations makes lactose tolerance likely
   else return Number(userTraitAssociatedAlleles) > 0 ? 85 : 15;
});

// These are useful to a message explaining (for equalEffect charts) how many alleles associated with the-trait-that-this-chart-is-about
// the user has at each of the rsid positions we have looked at.
const rsidsWithTwoAssociatedAlleles = computed(() => {
   return Object.values(allelesAssociatedWithTraitByRsid.value).filter((number) => number === 2).length;
});
const rsidsWithOneAssociatedAllele = computed(() => {
   return Object.values(allelesAssociatedWithTraitByRsid.value).filter((number) => number === 1).length;
});
const rsidsWithNoAssociatedAlleles = computed(() => {
   return Object.values(allelesAssociatedWithTraitByRsid.value).filter((number) => number === 0).length;
});

const ariaMarkerPositionDescription = computed(() => {
   if (markerPosition.value < 25) return $t("dna-testing.trait_reports.less_likely");
   else if (markerPosition.value < 50) return $t("dna-testing.trait_reports.slightly_less_likely");
   else if (markerPosition.value > 75) return $t("dna-testing.trait_reports.more_likely");
   else if (markerPosition.value > 50) return $t("dna-testing.trait_reports.slightly_more_likely");
   return $t("dna-testing.trait_reports.equal_likelihood");
});
</script>

<style scoped lang="scss"></style>
